import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Form, FormField, FormItem, FormLabel, FormControl, FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { upiFormSchema } from '../utils/validation';
import { UPI_APPS } from '../types/index';

/**
 * UPI payment form.
 * @param {{ onSubmit: Function, isProcessing: boolean }} props
 */
export function UpiPaymentForm({ onSubmit, isProcessing }) {
    const form = useForm({
        resolver: zodResolver(upiFormSchema),
        defaultValues: { upiId: '' },
        mode: 'onTouched',
    });

    const setUpiApp = (appId) => {
        const suffixes = { gpay: '@okaxis', phonepe: '@ybl', paytm: '@paytm', bhim: '@upi' };
        const currentId = form.getValues('upiId');
        const base = currentId.split('@')[0];
        form.setValue('upiId', base ? `${base}${suffixes[appId] || '@upi'}` : `mobilenumber${suffixes[appId] || '@upi'}`);
        form.trigger('upiId');
    };

    return (
        <div className="space-y-5" data-testid="upi-payment-form">
            {/* Quick-select UPI apps */}
            <div>
                <p className="text-sm text-slate-600 mb-3">Quick select your UPI app</p>
                <div className="grid grid-cols-4 gap-2">
                    {UPI_APPS.map((app) => (
                        <button
                            key={app.id}
                            type="button"
                            onClick={() => setUpiApp(app.id)}
                            data-testid={`upi-app-${app.id}`}
                            className="flex flex-col items-center gap-1 p-3 border border-slate-200 rounded-sm
                text-xs text-slate-600 font-medium hover:border-[#F87217] hover:text-[#F87217]
                hover:bg-[#F87217]/5 transition-all duration-200"
                        >
                            <span className="text-lg" aria-hidden="true">📱</span>
                            {app.name}
                        </button>
                    ))}
                </div>
            </div>

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-5" aria-label="UPI payment form">
                    <FormField
                        control={form.control}
                        name="upiId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel htmlFor="upiId">UPI ID</FormLabel>
                                <FormControl>
                                    <Input
                                        id="upiId"
                                        placeholder="yourname@upi"
                                        autoComplete="off"
                                        inputMode="email"
                                        data-testid="input-upi-id"
                                        aria-required="true"
                                        aria-describedby="upi-hint"
                                        {...field}
                                    />
                                </FormControl>
                                <p id="upi-hint" className="text-xs text-slate-400">
                                    Enter your registered UPI ID (e.g., 9876543210@paytm)
                                </p>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <Button
                        type="submit"
                        disabled={isProcessing}
                        className="w-full bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm btn-primary-glow font-semibold py-6"
                        data-testid="submit-upi-payment"
                    >
                        {isProcessing ? (
                            <span className="flex items-center gap-2">
                                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Processing...
                            </span>
                        ) : 'Pay via UPI'}
                    </Button>
                </form>
            </Form>
        </div>
    );
}

export default UpiPaymentForm;
